import { useEffect, useState, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useWebSocket } from 'context/WebSocketConnectionContext';
import { getTenantDetails } from 'utils/utils';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { getBillingCode } from 'services/timerServices';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'modules/BillingV2/config/queryClient';

export default function useAutomatedTime(case_id, case_name) {
  const { send, subscribe } = useWebSocket();
  const { firmDetails, profileData } = useUserDetailsContext();
  const [tenant_id] = getTenantDetails();
  const queryClient = useQueryClient();

  const [timerParameter, setTimerParameter] = useState({});
  const isMountedRef = useRef(false);

  const isActive = profileData?.automatic_time_tracking_active;

  const getActivityBillingCode = async (moduleName) => {
    try {
      const response = await getBillingCode(moduleName, true);
      if (response?.data?.mapping_type) {
        return response.data.mapping_type[0];
      }
    } catch (err) {}
    return null;
  };

  const onIdle = async () => {
    let oldTimerParameter;
    setTimerParameter((preVal) => {
      oldTimerParameter = preVal;
      return preVal;
    });
    // Check if still mounted and has active time entry
    if (!isMountedRef.current || !isActive || !timerParameter?.time_entry_sk) {
      return;
    }

    const billingCode = await getActivityBillingCode(oldTimerParameter?.module);

    // Double-check after async operation
    if (isMountedRef.current && isActive && oldTimerParameter?.time_entry_sk) {
      send({
        action: 'timeentry',
        time_entry_type: 'idle_start',
        tenant_id: tenant_id,
        user_id: firmDetails?.firm_user_id,
        case_id: case_id,
        time_entry_sk: oldTimerParameter?.time_entry_sk,
        module_name: oldTimerParameter?.module,
        activity_type: billingCode?.activity_type,
        activity_type_code: billingCode?.activity_type_code,
        activity_type_id: billingCode?.activity_type_id,
      });
    }
  };

  const onActive = () => {
    // Check if still mounted and has active time entry with idle tracking
    if (!isMountedRef.current || !isActive || !timerParameter?.time_entry_sk || !timerParameter?.idle_time_entry_sk) {
      return;
    }

    send({
      action: 'timeentry',
      time_entry_type: 'idle_end',
      tenant_id: tenant_id,
      user_id: firmDetails?.firm_user_id,
      case_id: case_id,
      time_entry_sk: timerParameter?.time_entry_sk,
      idle_time_entry_sk: timerParameter?.idle_time_entry_sk,
    });
  };

  const onOperationBreak = () => {
    // delay added to avoid calling operation break before the timer end operation
    setTimeout(() => {
      if (isActive) {
        send({
          action: 'timeentry',
          time_entry_type: 'operation_break',
        });
        setTimerParameter({});
        reset();
      }
    }, 2000);
  };

  const { reset, start, pause } = useIdleTimer({
    onIdle,
    onActive,
    timeout: 10 * 60 * 1000,
    throttle: 500,
  });

  useEffect(() => {
    const handler = (data) => {
      setTimerParameter((preVal) => ({
        ...preVal,
        time_entry_sk: data?.body?.time_entry_sk || preVal?.time_entry_sk,
        idle_time_entry_sk: data?.body?.idle_time_entry_sk || preVal?.idle_time_entry_sk,
      }));
    };

    const unsubscribe = subscribe('time_entry_response', handler);

    return () => {
      unsubscribe?.();
    };
  }, []);

  const onMountUnMount = async (moduleName, action, caseId, caseName) => {
    let oldData = {};
    let billingCode = {};
    setTimerParameter((preVal) => {
      oldData = preVal;
      return preVal;
    });
    if (action === 'mounted' && isActive) {
      isMountedRef.current = true;
      billingCode = await getActivityBillingCode(moduleName);
      send({
        action: 'timeentry',
        time_entry_type: 'time_entry_start',
        tenant_id: tenant_id,
        user_id: firmDetails?.firm_user_id,
        case_name: caseName || case_name,
        case_id: caseId || case_id,
        module_name: moduleName,
        activity_type: billingCode?.activity_type,
        activity_type_code: billingCode?.activity_type_code,
        activity_type_id: billingCode?.activity_type_id,
      });
      start();
    } else if (action === 'unmounted' && isActive && oldData?.time_entry_sk) {
      // Mark as unmounted first to prevent any callbacks from executing
      isMountedRef.current = false;
      // Pause the timer to prevent onIdle from being called
      pause();
      send({
        action: 'timeentry',
        time_entry_type: 'time_entry_end',
        tenant_id: tenant_id,
        user_id: firmDetails?.firm_user_id,
        case_name: caseName || case_name,
        case_id: caseId || case_id,
        time_entry_sk: oldData?.time_entry_sk,
      });
      // Only invalidate time-entry queries when a specific case was being tracked.
      // Skipping this for the global file manager (no case_id) prevents triggering
      // a list-time-entries refetch in the layout's TimerManager on every navigation.
      if (caseId || case_id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      }
      setTimerParameter({});
      reset();
    }
    if (action === 'mounted') {
      setTimerParameter((preVal) => ({
        ...preVal,
        module: moduleName,
        activity_type: billingCode?.activity_type,
        activity_type_code: billingCode?.activity_type_code,
        activity_type_id: billingCode?.activity_type_id,
      }));
    }
  };

  return { onMountUnMount, onOperationBreak };
}
